import React, { useState, useEffect } from 'react';
import './Metrics.css';
import axios from 'axios';
import { API_URL } from '../config';

function Metrics({ onBack }) {
  const [metrics, setMetrics] = useState(null);
  const [progress, setProgress] = useState(null);
  const [topics, setTopics] = useState([]);
  const [errorDetails, setErrorDetails] = useState([]);
  const [errorPatterns, setErrorPatterns] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview', 'errors', 'patterns'
  const [showFilters, setShowFilters] = useState(false);
  
  // Filtros
  const [filterDomain, setFilterDomain] = useState('');
  const [filterFocus, setFilterFocus] = useState('');
  const [filterDifficulty, setFilterDifficulty] = useState('');
  const [filterTense, setFilterTense] = useState('');

  // Paginação de tópicos
  const [topicsPage, setTopicsPage] = useState(1);
  const [topicsLimit] = useState(20);
  const [hasMoreTopics, setHasMoreTopics] = useState(false);
  const [isLoadingMoreTopics, setIsLoadingMoreTopics] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async (page = 1, append = false) => {
    try {
      if (!append) {
        setIsLoading(true);
        setError(null);
      } else {
        setIsLoadingMoreTopics(true);
      }

      // Construir query string com filtros e paginação
      const params = new URLSearchParams();
      params.append('limit', topicsLimit.toString());
      params.append('page', page.toString());
      if (filterDomain) params.append('domain', filterDomain);
      if (filterFocus) params.append('focus', filterFocus);
      if (filterDifficulty) params.append('difficulty', filterDifficulty);
      if (filterTense) params.append('tense', filterTense);
      
      const topicsUrl = `${API_URL}/v1/topics/search?${params.toString()}`;

      // Carregar métricas, progresso, tópicos, erros detalhados e padrões em paralelo
      const [metricsRes, progressRes, topicsRes, errorsRes, patternsRes] =
        await Promise.all([
          axios.get(`${API_URL}/v1/metrics`),
          axios.get(`${API_URL}/v1/progress`),
          axios.get(topicsUrl),
          axios.get(`${API_URL}/v1/progress/errors?limit=100`),
          axios.get(`${API_URL}/v1/progress/patterns`),
        ]);

      if (!append) {
        setMetrics(metricsRes.data);
        setProgress(progressRes.data);
        setErrorDetails(errorsRes.data);
        setErrorPatterns(patternsRes.data);
      }

      // O endpoint retorna um objeto paginado com items, total_items, page, has_next_page, etc
      const topicsData = topicsRes.data.items || [];
      const totalItems = topicsRes.data.total_items || topicsRes.data.totalItems || 0;
      const currentPage = topicsRes.data.page || page;
      const totalPages = topicsRes.data.totalPages || topicsRes.data.total_pages || Math.ceil(totalItems / topicsLimit);
      const hasNextPage = topicsRes.data.has_next_page !== undefined 
        ? topicsRes.data.has_next_page 
        : (currentPage < totalPages);

      if (append) {
        // Adicionar novos tópicos à lista existente
        setTopics(prevTopics => [...prevTopics, ...topicsData]);
      } else {
        // Substituir a lista de tópicos
        setTopics(topicsData);
        setTopicsPage(1);
      }

      // Verificar se há mais páginas
      setHasMoreTopics(hasNextPage);
      setTopicsPage(currentPage);
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
      if (!append) {
        setError('Erro ao carregar métricas. Tente novamente.');
      }
    } finally {
      setIsLoading(false);
      setIsLoadingMoreTopics(false);
    }
  };

  const getTopicInfo = (topicId) => {
    return topics.find((t) => t.id === topicId) || null;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Nunca';
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR');
  };

  const getErrorTypeLabel = (errorType) => {
    const labels = {
      tense: 'Tempo Verbal',
      structure: 'Estrutura',
      pronunciation: 'Pronúncia',
    };
    return labels[errorType] || errorType;
  };

  const getErrorSubtypeLabel = (subtype) => {
    const labels = {
      present_vs_past_continuous: 'Presente vs Passado Contínuo',
      past_vs_present_continuous: 'Passado vs Presente Contínuo',
      missing_going_to: 'Faltando "going to"',
      missing_will: 'Faltando "will"',
      missing_present_perfect: 'Faltando Present Perfect',
      wrong_tense: 'Tempo Verbal Errado',
      missing_auxiliary: 'Faltando Auxiliar',
      wrong_word_order: 'Ordem de Palavras Errada',
      missing_word: 'Palavra Faltando',
      structure_error: 'Erro de Estrutura',
      low_similarity: 'Baixa Similaridade',
      unknown: 'Desconhecido',
    };
    return labels[subtype] || subtype;
  };

  const getDomainLabel = (domain) => {
    if (!domain) return '';
    const labels = {
      general: 'Geral',
      computing: 'Computação',
      literature: 'Literatura',
    };
    return labels[domain] || domain;
  };

  // Obter valores únicos para os filtros (usar todos os tópicos, não apenas os filtrados)
  const [allTopicsForFilters, setAllTopicsForFilters] = useState([]);

  useEffect(() => {
    // Carregar todos os tópicos para os filtros (sem filtros aplicados)
    // Usar um limit maior para obter mais tópicos para os filtros
    axios.get(`${API_URL}/v1/topics/search?limit=100&page=1`).then((response) => {
      // O endpoint retorna um objeto paginado com items
      const topicsData = response.data.items || response.data || [];
      setAllTopicsForFilters(Array.isArray(topicsData) ? topicsData : []);
    }).catch(() => {
      setAllTopicsForFilters(topics);
    });
  }, []);

  const getUniqueValues = (key) => {
    const source = allTopicsForFilters.length > 0 ? allTopicsForFilters : topics;
    return [...new Set(source.map(t => t[key]).filter(val => val != null && val !== ''))].sort();
  };

  // Filtrar métricas de tópicos baseado nos filtros (filtrar no frontend pois vem do endpoint de métricas)
  const filterTopicMetrics = (topicList) => {
    return topicList.filter((topicMetric) => {
      const topicInfo = getTopicInfo(topicMetric.topicId);
      if (!topicInfo) return false;

      if (filterDomain && topicInfo.domain !== filterDomain) return false;
      if (filterFocus && topicInfo.focus !== filterFocus) return false;
      if (filterDifficulty && topicInfo.difficulty !== filterDifficulty) return false;
      if (filterTense && topicInfo.tense !== filterTense) return false;

      return true;
    });
  };

  // Filtrar erros detalhados baseado nos filtros
  const filterErrorDetails = (errors) => {
    return errors.filter((error) => {
      const topicInfo = getTopicInfo(error.topic_id);
      if (!topicInfo) return false;

      if (filterDomain && topicInfo.domain !== filterDomain) return false;
      if (filterFocus && error.focus !== filterFocus) return false;
      if (filterDifficulty && topicInfo.difficulty !== filterDifficulty) return false;
      if (filterTense && topicInfo.tense !== filterTense) return false;

      return true;
    });
  };

  const clearFilters = () => {
    setFilterDomain('');
    setFilterFocus('');
    setFilterDifficulty('');
    setFilterTense('');
  };

  // Recarregar dados quando filtros mudarem
  useEffect(() => {
    if (!isLoading) {
      loadData(1, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterDomain, filterFocus, filterDifficulty, filterTense]);

  // Função para carregar mais tópicos
  const loadMoreTopics = async () => {
    if (!isLoadingMoreTopics && hasMoreTopics) {
      await loadData(topicsPage + 1, true);
    }
  };

  // Calcular métricas filtradas
  const filteredTopicMetrics = metrics?.topics ? filterTopicMetrics(metrics.topics) : [];
  const filteredErrorDetails = filterErrorDetails(errorDetails);

  if (isLoading) {
    return (
      <div className="metrics-container">
        <div className="loading">Carregando métricas...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="metrics-container">
        <div className="error">{error}</div>
        <button className="btn btn-primary" onClick={loadData}>
          Tentar novamente
        </button>
      </div>
    );
  }

  return (
    <div className="metrics-container">
      <div className="metrics-header">
        <h1>Métricas e Progresso</h1>
        <div className="header-actions">
          <button 
            className="btn btn-secondary" 
            onClick={() => setShowFilters(!showFilters)}
          >
            {showFilters ? '✕ Fechar Filtros' : '🔍 Filtros'}
          </button>
          <button className="btn btn-secondary" onClick={onBack}>
            ← Voltar para Prática
          </button>
        </div>
      </div>

      {showFilters && (
        <div className="filters-panel">
          <div className="filters-header">
            <h3>Filtros</h3>
            <button className="btn btn-link" onClick={clearFilters}>
              Limpar todos
            </button>
          </div>
          <div className="filters-grid">
            <div className="filter-group">
              <label>Domínio:</label>
              <select 
                value={filterDomain} 
                onChange={(e) => setFilterDomain(e.target.value)}
                className="filter-select"
              >
                <option value="">Todos</option>
                {getUniqueValues('domain').filter(domain => domain).map(domain => (
                  <option key={domain} value={domain}>
                    {domain === 'general' ? 'Geral' : 
                     domain === 'computing' ? 'Computação' : 
                     domain === 'literature' ? 'Literatura' : domain}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Padrão Gramatical:</label>
              <select 
                value={filterFocus} 
                onChange={(e) => setFilterFocus(e.target.value)}
                className="filter-select"
              >
                <option value="">Todos</option>
                {getUniqueValues('focus').filter(focus => focus).map(focus => (
                  <option key={focus} value={focus}>
                    {focus.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Dificuldade:</label>
              <select 
                value={filterDifficulty} 
                onChange={(e) => setFilterDifficulty(e.target.value)}
                className="filter-select"
              >
                <option value="">Todas</option>
                {getUniqueValues('difficulty').filter(difficulty => difficulty).map(difficulty => (
                  <option key={difficulty} value={difficulty}>
                    {difficulty === 'easy' ? 'Fácil' : 
                     difficulty === 'medium' ? 'Médio' : 
                     difficulty === 'hard' ? 'Difícil' : difficulty}
                  </option>
                ))}
              </select>
            </div>

            <div className="filter-group">
              <label>Tempo Verbal:</label>
              <select 
                value={filterTense} 
                onChange={(e) => setFilterTense(e.target.value)}
                className="filter-select"
              >
                <option value="">Todos</option>
                {getUniqueValues('tense').filter(tense => tense).map(tense => (
                  <option key={tense} value={tense}>
                    {tense === 'present' ? 'Presente' : 
                     tense === 'past' ? 'Passado' : 
                     tense === 'future' ? 'Futuro' : 
                     tense === 'present-perfect' ? 'Present Perfect' : tense}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="filters-info">
            <span>
              {filteredTopicMetrics.length} de {metrics?.topics?.length || 0} tópicos
              {(filterDomain || filterFocus || filterDifficulty || filterTense) && (
                <span className="filters-active"> (filtrados)</span>
              )}
            </span>
          </div>
        </div>
      )}

      {/* Tabs de navegação */}
      <div className="metrics-tabs">
        <button
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Visão Geral
        </button>
        <button
          className={`tab ${activeTab === 'errors' ? 'active' : ''}`}
          onClick={() => setActiveTab('errors')}
        >
          Histórico de Erros
        </button>
        <button
          className={`tab ${activeTab === 'patterns' ? 'active' : ''}`}
          onClick={() => setActiveTab('patterns')}
        >
          Padrões de Erro
        </button>
      </div>

      {activeTab === 'overview' && (
        <>
          {/* Estatísticas Gerais */}
      <div className="stats-section">
        <h2>Estatísticas Gerais</h2>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-value">{metrics?.totalAttempts || 0}</div>
            <div className="stat-label">Total de Tentativas</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{metrics?.totalCorrect || 0}</div>
            <div className="stat-label">Acertos</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">
              {metrics?.successRate
                ? `${metrics.successRate.toFixed(1)}%`
                : '0%'}
            </div>
            <div className="stat-label">Taxa de Sucesso</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{metrics?.topicsPracticed || 0}</div>
            <div className="stat-label">Tópicos Praticados</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">
              {metrics?.averageAttemptsUntilSuccess
                ? metrics.averageAttemptsUntilSuccess.toFixed(1)
                : '0'}
            </div>
            <div className="stat-label">Média de Tentativas até Acertar</div>
          </div>
        </div>
      </div>

      {/* Análise de Erros */}
      {progress && (
        <div className="errors-section">
          <h2>Análise de Erros</h2>
          <div className="errors-grid">
            <div className="error-card">
              <div className="error-type">Tempo Verbal</div>
              <div className="error-count">{progress.errorCounts?.tense || 0}</div>
            </div>
            <div className="error-card">
              <div className="error-type">Estrutura</div>
              <div className="error-count">
                {progress.errorCounts?.structure || 0}
              </div>
            </div>
            <div className="error-card">
              <div className="error-type">Pronúncia</div>
              <div className="error-count">
                {progress.errorCounts?.pronunciation || 0}
              </div>
            </div>
          </div>
          {progress.mostRecurrentError && (
            <div className="most-recurrent-error">
              <strong>Erro mais recorrente:</strong>{' '}
              {getErrorTypeLabel(progress.mostRecurrentError)}
            </div>
          )}
          {progress.consecutiveErrors > 0 && (
            <div className="consecutive-errors">
              <strong>Erros consecutivos:</strong> {progress.consecutiveErrors}
            </div>
          )}
        </div>
      )}

      {/* Métricas por Tópico */}
      {metrics?.topics && metrics.topics.length > 0 && (
        <div className="topics-section">
          <h2>Métricas por Tópico</h2>
          {filteredTopicMetrics.length === 0 && (filterDomain || filterFocus || filterDifficulty || filterTense) && (
            <div className="empty-state">
              <p>Nenhum tópico encontrado com os filtros selecionados.</p>
            </div>
          )}
          <div className="topics-list">
            {filteredTopicMetrics.map((topicMetric) => {
              const topicInfo = getTopicInfo(topicMetric.topicId);
              const successRate =
                topicMetric.attempts > 0
                  ? ((topicMetric.correctAttempts / topicMetric.attempts) *
                      100).toFixed(1)
                  : 0;

              return (
                <div key={topicMetric.topicId} className="topic-metric-card">
                  <div className="topic-metric-header">
                    <div className="topic-sentence">
                      {topicInfo?.sentence || topicMetric.topicId}
                    </div>
                    {topicInfo && (
                      <div className="topic-meta">
                        {topicInfo.domain && (
                          <span className="topic-domain">
                            {getDomainLabel(topicInfo.domain)}
                          </span>
                        )}
                        {topicInfo.focus && (
                          <span className="topic-focus">
                            {topicInfo.focus.replace(/_/g, ' ')}
                          </span>
                        )}
                        {topicInfo.difficulty && (
                          <span className={`topic-difficulty ${topicInfo.difficulty}`}>
                            {topicInfo.difficulty}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="topic-metric-stats">
                    <div className="topic-stat">
                      <span className="topic-stat-label">Tentativas:</span>
                      <span className="topic-stat-value">
                        {topicMetric.attempts}
                      </span>
                    </div>
                    <div className="topic-stat">
                      <span className="topic-stat-label">Acertos:</span>
                      <span className="topic-stat-value">
                        {topicMetric.correctAttempts}
                      </span>
                    </div>
                    <div className="topic-stat">
                      <span className="topic-stat-label">Taxa de Sucesso:</span>
                      <span className="topic-stat-value">{successRate}%</span>
                    </div>
                    {topicMetric.averageAttemptsUntilSuccess > 0 && (
                      <div className="topic-stat">
                        <span className="topic-stat-label">
                          Média até Acertar:
                        </span>
                        <span className="topic-stat-value">
                          {topicMetric.averageAttemptsUntilSuccess.toFixed(1)}
                        </span>
                      </div>
                    )}
                    {topicMetric.lastAttempt && (
                      <div className="topic-stat">
                        <span className="topic-stat-label">Última tentativa:</span>
                        <span className="topic-stat-value">
                          {formatDate(topicMetric.lastAttempt)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {hasMoreTopics && (
            <div className="load-more-container">
              <button
                className="btn btn-secondary"
                onClick={loadMoreTopics}
                disabled={isLoadingMoreTopics}
              >
                {isLoadingMoreTopics ? 'Carregando...' : 'Carregar mais tópicos'}
              </button>
            </div>
          )}
        </div>
      )}

          {(!metrics?.topics || metrics.topics.length === 0) && (
            <div className="empty-state">
              <p>Você ainda não praticou nenhum tópico.</p>
              <p>Comece a praticar para ver suas métricas aqui!</p>
            </div>
          )}
        </>
      )}

      {activeTab === 'errors' && (
        <div className="errors-details-section">
          <h2>Histórico de Erros</h2>
          {filteredErrorDetails && filteredErrorDetails.length > 0 ? (
            <div className="errors-list">
              {filteredErrorDetails.map((error) => {
                const topicInfo = getTopicInfo(error.topic_id);
                return (
                  <div key={error.id} className="error-detail-card">
                    <div className="error-detail-header">
                      <div className="error-detail-type">
                        {getErrorTypeLabel(error.error_type)}
                        {error.error_subtype && (
                          <span className="error-subtype">
                            {' '}
                            - {getErrorSubtypeLabel(error.error_subtype)}
                          </span>
                        )}
                      </div>
                      <div className="error-detail-date">
                        {formatDate(error.created_at)}
                      </div>
                    </div>
                    {topicInfo && (
                      <div className="error-detail-topic">
                        <strong>Tópico:</strong> {topicInfo.sentence}
                        {topicInfo.domain && (
                          <span className="topic-domain-badge">
                            {getDomainLabel(topicInfo.domain)}
                          </span>
                        )}
                        {error.focus && (
                          <span className="topic-focus-badge">
                            {error.focus.replace(/_/g, ' ')}
                          </span>
                        )}
                      </div>
                    )}
                    <div className="error-detail-texts">
                      <div className="error-expected">
                        <strong>Esperado:</strong> {error.expected_text}
                      </div>
                      <div className="error-spoken">
                        <strong>Falado:</strong> {error.spoken_text}
                      </div>
                    </div>
                    <div className="error-feedback">{error.feedback}</div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="empty-state">
              <p>
                {errorDetails.length > 0 && (filterDomain || filterFocus || filterDifficulty || filterTense)
                  ? 'Nenhum erro encontrado com os filtros selecionados.'
                  : 'Nenhum erro registrado ainda.'}
              </p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'patterns' && errorPatterns && (
        <div className="patterns-section">
          <h2>Análise de Padrões de Erro</h2>

          {/* Erros por Domínio */}
          {errorPatterns.byDomain && errorPatterns.byDomain.length > 0 && (
            <div className="pattern-group">
              <h3>Erros por Domínio</h3>
              <div className="pattern-list">
                {errorPatterns.byDomain.map((item, idx) => (
                  <div key={idx} className="pattern-item">
                    <span className="pattern-label">
                      {getDomainLabel(item.domain)}
                    </span>
                    <span className="pattern-count">{item.count} erros</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Erros por Padrão Gramatical */}
          {errorPatterns.byFocus && errorPatterns.byFocus.length > 0 && (
            <div className="pattern-group">
              <h3>Erros por Padrão Gramatical</h3>
              <div className="pattern-list">
                {errorPatterns.byFocus.filter(item => item.focus).map((item, idx) => (
                  <div key={idx} className="pattern-item">
                    <span className="pattern-label">
                      {item.focus.replace(/_/g, ' ')}
                    </span>
                    <span className="pattern-count">{item.count} erros</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Erros por Subtipo */}
          {errorPatterns.bySubtype && errorPatterns.bySubtype.length > 0 && (
            <div className="pattern-group">
              <h3>Tipos de Erro Mais Comuns</h3>
              <div className="pattern-list">
                {errorPatterns.bySubtype
                  .sort((a, b) => b.count - a.count)
                  .map((item, idx) => (
                    <div key={idx} className="pattern-item">
                      <span className="pattern-label">
                        {getErrorSubtypeLabel(item.subtype)}
                      </span>
                      <span className="pattern-count">{item.count} vezes</span>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Tópicos com Mais Erros */}
          {errorPatterns.byTopic && errorPatterns.byTopic.length > 0 && (
            <div className="pattern-group">
              <h3>Tópicos com Mais Erros</h3>
              <div className="pattern-list">
                {errorPatterns.byTopic.map((item, idx) => {
                  const topicInfo = getTopicInfo(item.topicId);
                  return (
                    <div key={idx} className="pattern-item">
                      <span className="pattern-label">
                        {topicInfo
                          ? topicInfo.sentence
                          : `Tópico ${item.topicId}`}
                      </span>
                      <span className="pattern-count">{item.count} erros</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Erros por Tipo e Tópico */}
          {errorPatterns.byTypeAndTopic && errorPatterns.byTypeAndTopic.length > 0 && (
            <div className="pattern-group">
              <h3>Erros por Tipo e Tópico</h3>
              <div className="error-topic-relation">
                {['tense', 'structure', 'pronunciation'].map((errorType) => {
                  const errorsForType = errorPatterns.byTypeAndTopic.filter(
                    (e) => e.errorType === errorType
                  );
                  if (errorsForType.length === 0) return null;
                  
                  return (
                    <div key={errorType} className="error-type-group">
                      <h4>{getErrorTypeLabel(errorType)}</h4>
                      <div className="topic-errors-list">
                        {errorsForType.map((item, idx) => {
                          const topicInfo = getTopicInfo(item.topicId);
                          return (
                            <div key={idx} className="topic-error-item">
                              <div className="topic-error-sentence">
                                {topicInfo
                                  ? topicInfo.sentence
                                  : `Tópico ${item.topicId}`}
                              </div>
                              <span className="topic-error-count">
                                {item.count} erro{item.count !== 1 ? 's' : ''}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Erros por Subtipo e Tópico */}
          {errorPatterns.bySubtypeAndTopic && errorPatterns.bySubtypeAndTopic.length > 0 && (
            <div className="pattern-group">
              <h3>Erros Específicos por Tópico</h3>
              <div className="error-subtype-topic-relation">
                {errorPatterns.bySubtypeAndTopic.map((item, idx) => {
                  const topicInfo = getTopicInfo(item.topicId);
                  return (
                    <div key={idx} className="subtype-topic-item">
                      <div className="subtype-topic-header">
                        <span className="subtype-label">
                          {getErrorSubtypeLabel(item.errorSubtype)}
                        </span>
                        <span className="subtype-count">
                          {item.count} vez{item.count !== 1 ? 'es' : ''}
                        </span>
                      </div>
                      <div className="subtype-topic-sentence">
                        <strong>Tópico:</strong>{' '}
                        {topicInfo
                          ? topicInfo.sentence
                          : `Tópico ${item.topicId}`}
                        {topicInfo && (
                          <span className="subtype-topic-meta">
                            {' '}
                            ({topicInfo.domain && getDomainLabel(topicInfo.domain)}
                            {topicInfo.domain && topicInfo.focus && ' • '}
                            {topicInfo.focus?.replace(/_/g, ' ')})
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default Metrics;

